import React, { useEffect, useState } from "react";
import { useForm, useFieldArray, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import Datepicker from "react-tailwindcss-datepicker";
import Select from "react-select";
import { SlDrawer } from "@shoelace-style/shoelace/dist/react";
import API from "../../services/api";
import { langoptions } from "../../utils/constants";
import { Toast } from "../../shared/Toast";

const defaultOrder = {
  order_name: "",
  company: "",
  sales: "",
  internal: false,
  gst: false,
  dateRange: { startDate: null, endDate: null },
  lang_ids: [],
  state_ids: [],
  district_ids: [],
  constituency_ids: [],
  quantity: "",
  price: "",
};

const today = new Date();
today.setHours(0, 0, 0, 0);

const orderSchema = yup.object({
  order_name: yup.string().required("Order name is required"),
  company: yup.string().required("Company is required"),
  sales: yup.string().required("Sales Person is required"),
  internal: yup.boolean(),
  gst: yup.boolean(),
  dateRange: yup
    .object({
      startDate: yup
        .date()
        .typeError("Start date is required")
        .min(today, "Start date cannot be in the past")
        .required("Start date is required"),
      endDate: yup
        .date()
        .typeError("End date is required")
        .required("End date is required")
        .min(yup.ref("startDate"), "End date cannot be before start date"),
    })
    .required("Date range is required"),
  lang_ids: yup
    .array(yup.number())
    .min(1, "Language is required")
    .required("Language is required"),
  state_ids: yup.array(yup.number()).optional(),
  district_ids: yup.array(yup.number()).optional(),
  constituency_ids: yup.array(yup.number()).optional(),
  quantity: yup
    .number()
    .typeError("Quantity is required")
    .positive("Quantity must be positive")
    .integer("Quantity should be a whole number")
    .required("Quantity is required"),
  price: yup
    .number()
    .typeError("Price is required")
    .positive("Price must be positive")
    .required("Price is required"),
});

const schema = yup.object({
  orders: yup.array().of(orderSchema),
});


function MultiOrder({open, loadOrders, companies, sales}) {
    const [salesOptions] = useState([{label:"Choose Sales person", value:""},...sales])
    const [companyoptions] = useState([{label:"Select Company", value:""},...companies])
  const {
    control,
    register,
    handleSubmit,
    setValue,
    getValues,
    formState: { errors, isSubmitting },
    watch,
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: { orders: [defaultOrder] },
    mode: "onBlur",
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "orders",
  });

  const watchOrders = watch("orders");
  const [stateOptions, setStateOptions] = useState([[]]);
  const [districtOptions, setDistrictOptions] = useState([[]]);
  const [constituencyOptions, setConstituencyOptions] = useState([[]]);

  useEffect(() => {
    setStateOptions((prev) => {
      const copy = [...prev];
      while (copy.length < fields.length) copy.push([]);
      return copy.slice(0, fields.length);
    });
    setDistrictOptions((prev) => {
      const copy = [...prev];
      while (copy.length < fields.length) copy.push([]);
      return copy.slice(0, fields.length);
    });
    setConstituencyOptions((prev) => {
      const copy = [...prev];
      while (copy.length < fields.length) copy.push([]);
      return copy.slice(0, fields.length);
    });
  }, [fields.length]);

  const updateOptionsAt = (setter, index, value) => {
    setter((prev) => {
      const next = [...prev];
      next[index] = value;
      return next;
    });
  };

  const copyPreviousOrder = (index) => {
    if (index <= 0) return;
    const prevOrder = getValues(`orders.${index - 1}`);
    if (!prevOrder) return;

    Object.keys(defaultOrder).forEach((key) => {
      const prevValue = prevOrder[key];
      const value =
        Array.isArray(prevValue) && prevValue !== undefined
          ? [...prevValue]
          : prevValue && typeof prevValue === "object"
          ? { ...prevValue }
          : prevValue;

      setValue(`orders.${index}.${key}`, value, {
        shouldValidate: true,
        shouldDirty: true,
      });
    });

    updateOptionsAt(setStateOptions, index, stateOptions[index - 1] || []);
    updateOptionsAt(setDistrictOptions, index, districtOptions[index - 1] || []);
    updateOptionsAt(
      setConstituencyOptions,
      index,
      constituencyOptions[index - 1] || []
    );
  };

  const filterStatesByLangs = (stateIds, options, langs) =>
    (stateIds || []).filter((id) => {
      const matched = (options || []).find((opt) => opt.value === id);
      return matched && langs.includes(matched.lang_id);
    });

  const filterDistrictIdsByStates = (districtIds, options, states) =>
    (districtIds || []).filter((id) => {
      const matched = (options || []).find((opt) => opt.value === id);
      return matched && states.includes(matched.state_id);
    });

  const filterConstituencyIdsByDistricts = (
    constituencyIds,
    options,
    districts
  ) =>
    (constituencyIds || []).filter((id) => {
      const matched = (options || []).find((opt) => opt.value === id);
      return matched && districts.includes(matched.dist_id);
    });

  const handleLangChange = async (selected, index) => {
    const langs = selected?.map((item) => item.value) || [];
    const currentStates = watchOrders?.[index]?.state_ids || [];
    const currentDistricts = watchOrders?.[index]?.district_ids || [];
    const currentConstituencies = watchOrders?.[index]?.constituency_ids || [];

    const filteredStates = filterStatesByLangs(
      currentStates,
      stateOptions[index],
      langs
    );
    const filteredDistricts = filterDistrictIdsByStates(
      currentDistricts,
      districtOptions[index],
      filteredStates
    );
    const filteredConstituencies = filterConstituencyIdsByDistricts(
      currentConstituencies,
      constituencyOptions[index],
      filteredDistricts
    );

    setValue(`orders.${index}.lang_ids`, langs, { shouldValidate: true });
    setValue(`orders.${index}.state_ids`, filteredStates, {
      shouldValidate: true,
    });
    setValue(`orders.${index}.district_ids`, filteredDistricts, {
      shouldValidate: true,
    });
    setValue(`orders.${index}.constituency_ids`, filteredConstituencies, {
      shouldValidate: true,
    });
    updateOptionsAt(setStateOptions, index, stateOptions[index] || []);
    updateOptionsAt(setDistrictOptions, index, districtOptions[index] || []);
    updateOptionsAt(
      setConstituencyOptions,
      index,
      constituencyOptions[index] || []
    );

    if (!langs.length) return;
    try {
      const res = await API({
        url: "/location/getStatesByLangId",
        method: "POST",
        responseType: "json",
        data: langs,
      });
      const options =
        res?.data?.map((item) => ({
          value: item.state_id,
          label: item.state_name,
          lang_id: item.lang_id,
        })) || [];
      updateOptionsAt(setStateOptions, index, options);
    } catch (error) {
      console.error("Failed to load states", error);
    }
  };

  const handleStateChange = async (selected, index) => {
    const states = selected?.map((item) => item.value) || [];
    const langs = watchOrders?.[index]?.lang_ids || [];
    const currentDistricts = watchOrders?.[index]?.district_ids || [];
    const filteredDistrictOptions = (districtOptions[index] || []).filter((opt) =>
      states.includes(opt.state_id)
    );
    const filteredDistricts = filterDistrictIdsByStates(
      currentDistricts,
      filteredDistrictOptions,
      states
    );
    const currentConstituencies = watchOrders?.[index]?.constituency_ids || [];
    const filteredConstituencyOptions = (constituencyOptions[index] || []).filter(
      (opt) => filteredDistricts.includes(opt.dist_id)
    );
    const filteredConstituencies = filterConstituencyIdsByDistricts(
      currentConstituencies,
      filteredConstituencyOptions,
      filteredDistricts
    );

    setValue(`orders.${index}.state_ids`, states, { shouldValidate: true });
    setValue(`orders.${index}.district_ids`, filteredDistricts, {
      shouldValidate: true,
    });
    setValue(`orders.${index}.constituency_ids`, filteredConstituencies, {
      shouldValidate: true,
    });
    updateOptionsAt(setDistrictOptions, index, filteredDistrictOptions);
    updateOptionsAt(setConstituencyOptions, index, filteredConstituencyOptions);

    if (!states.length) return;
    try {
      const res = await API({
        url: "/location/getDistrictsByStateId",
        method: "POST",
        responseType: "json",
        data: { states, langs },
      });
      const options =
        res?.data?.map((item) => ({
          value: item.dist_id,
          label: item.dist_name,
          state_id: item.state_id,
          lang_id: item.lang_id,
        })) || [];
      updateOptionsAt(setDistrictOptions, index, options);
    } catch (error) {
      console.error("Failed to load districts", error);
    }
  };

  const handleDistrictChange = async (selected, index) => {
    const districts = selected?.map((item) => item.value) || [];
    const langs = watchOrders?.[index]?.lang_ids || [];
    const currentConstituencies = watchOrders?.[index]?.constituency_ids || [];
    const filteredConstituencies = filterConstituencyIdsByDistricts(
      currentConstituencies,
      constituencyOptions[index],
      districts
    );

    setValue(`orders.${index}.district_ids`, districts, {
      shouldValidate: true,
    });
    setValue(`orders.${index}.constituency_ids`, filteredConstituencies, {
      shouldValidate: true,
    });
    updateOptionsAt(setConstituencyOptions, index, constituencyOptions[index] || []);

    if (!districts.length) return;
    try {
      const res = await API({
        url: "/location/getConstituentsnew",
        method: "POST",
        responseType: "json",
        data: { districts, langs },
      });
      const options =
        res?.data?.map((item) => ({
          value: item.const_id,
          label: item.const_name,
          dist_id: item.dist_id,
          lang_id: item.lang_id,
        })) || [];
      updateOptionsAt(setConstituencyOptions, index, options);
    } catch (error) {
      console.error("Failed to load constituencies", error);
    }
  };

  const onSubmit = async (data) => {
    try {
      const payload = data.orders.map((order) => ({
        ...order,
        start_date: order?.dateRange?.startDate,
        end_date: order?.dateRange?.endDate,
      }));

      console.log("Submitting orders", payload);

      let res = await API({
        url: "/order/create-multi-order",
        responseType: "json",
        method: "POST",
        data: {
            orders:payload},
      });
      if (res.code === 200) {
        Toast('success', 'Order Created Successfully!')
        loadOrders();
      }
    } catch (error) {
      console.error(error, "Error Submitting Multi Order");
    }
  };
  function handleRequestClose(event) {
    if (event.detail.source === "overlay") {
      event.preventDefault();
      return
    }
    loadOrders()
  }
  return (
    <SlDrawer open={open} onSlRequestClose={handleRequestClose} style={{'--size':'50vw'}}>
    <div className="min-h-screen bg-gray-50 text-slate-800 p-6">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="text-sm text-slate-500">Bulk Magazine create orders</p>
            <h1 className="text-2xl font-semibold">Multi Order Entry</h1>
          </div>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {fields.map((field, index) => {
            const orderError = errors?.orders?.[index] || {};
            const currentOrder = watchOrders?.[index] || {};
            const summaryTitle =
              currentOrder?.order_name?.trim() || "Add order name";
            const scheduleLabel =
              currentOrder?.dateRange?.startDate &&
              currentOrder?.dateRange?.endDate
                ? `${currentOrder.dateRange.startDate} → ${currentOrder.dateRange.endDate}`
                : "Set schedule";

            return (
              <details
                key={field.id}
                open
                className="group rounded-lg border border-slate-200 bg-white shadow-sm"
              >
                <summary className="flex items-center justify-between cursor-pointer px-4 py-3">
                  <div className="flex items-start gap-3">
                    <span className="mt-0.5 inline-flex h-8 w-8 items-center justify-center rounded-full bg-slate-100 text-slate-700 transition-transform group-open:rotate-180">
                      ▾
                    </span>
                    <div className="flex flex-col">
                      <span className="font-medium text-slate-800">
                        {summaryTitle}
                      </span>
                      <span className="text-xs text-slate-500">
                        {scheduleLabel}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {index > 0 && (
                      <button
                        type="button"
                        onClick={() => copyPreviousOrder(index)}
                        className="text-sm text-blue-600 hover:text-blue-700"
                      >
                        Copy above
                      </button>
                    )}
                    {fields.length > 1 && (
                      <button
                        type="button"
                        onClick={() => remove(index)}
                        className="text-sm text-red-600 hover:text-red-700"
                      >
                        Remove
                      </button>
                    )}
                  </div>
                </summary>

                <div className="border-t border-slate-200 px-4 py-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div className="flex flex-col gap-1">
                      <label className="text-sm text-slate-700">
                        Order name
                      </label>
                      <input
                        type="text"
                        {...register(`orders.${index}.order_name`)}
                        className="form-control"
                        placeholder="Quarterly push campaign"
                      />
                      {orderError.order_name && (
                        <span className="text-xs text-red-600">
                          {orderError.order_name.message}
                        </span>
                      )}
                    </div>
                    <div className="flex flex-col gap-1">
                      <label className="text-sm text-slate-700">Company</label>
                      <Controller
                        control={control}
                        name={`orders.${index}.company`}
                        render={({ field }) => (
                          <Select
                            {...field}
                            className="text-sm"
                            classNamePrefix="react-select"
                            options={companyoptions}
                            value={
                              companyoptions.find(
                                (option) => option.value === field.value
                              ) || companyoptions[0]
                            }
                            onChange={(option) =>
                              field.onChange(option?.value || "")
                            }
                            isSearchable
                          />
                        )}
                      />
                      {orderError.company && (
                        <span className="text-xs text-red-600">
                          {orderError.company.message}
                        </span>
                      )}
                    </div>

                    <div className="flex flex-col gap-1">
                      <label className="text-sm text-slate-700">Sales</label>
                      <Controller
                        control={control}
                        name={`orders.${index}.sales`}
                        render={({ field }) => (
                          <Select
                            {...field}
                            className="text-sm"
                            classNamePrefix="react-select"
                            options={salesOptions}
                            value={
                              salesOptions.find(
                                (option) => option.value === field.value
                              ) || salesOptions[0]
                            }
                            onChange={(option) =>
                              field.onChange(option?.value || "")
                            }
                            isSearchable
                          />
                        )}
                      />
                      {orderError.sales && (
                        <span className="text-xs text-red-600">
                          {orderError.sales.message}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="flex flex-col gap-1">
                      <label className="text-sm text-slate-700">
                        Languages
                      </label>
                      <Controller
                        control={control}
                        name={`orders.${index}.lang_ids`}
                        render={({ field }) => (
                          <Select
                            {...field}
                            isMulti
                            className="text-sm"
                            classNamePrefix="react-select"
                            options={langoptions}
                            value={langoptions.filter((opt) =>
                              (field.value || []).includes(opt.value)
                            )}
                            onChange={(selected) =>
                              handleLangChange(selected, index)
                            }
                            closeMenuOnSelect={false}
                            placeholder="Select languages"
                          />
                        )}
                      />
                      {orderError.lang_ids && (
                        <span className="text-xs text-red-600">
                          {orderError.lang_ids.message}
                        </span>
                      )}
                    </div>

                    <div className="flex flex-col gap-1">
                      <label className="text-sm text-slate-700">States</label>
                      <Controller
                        control={control}
                        name={`orders.${index}.state_ids`}
                        render={({ field }) => (
                          <Select
                            {...field}
                            isMulti
                            className="text-sm"
                            classNamePrefix="react-select"
                            options={stateOptions[index] || []}
                            value={(stateOptions[index] || []).filter((opt) =>
                              (field.value || []).includes(opt.value)
                            )}
                            onChange={(selected) =>
                              handleStateChange(selected, index)
                            }
                            closeMenuOnSelect={false}
                            placeholder="Select states"
                          />
                        )}
                      />
                    </div>

                  </div>

                  <div className="mt-4 grid grid-cols-1 gap-4">
                    <div className="flex flex-col gap-1">
                      <label className="text-sm text-slate-700">Districts</label>
                      <Controller
                        control={control}
                        name={`orders.${index}.district_ids`}
                        render={({ field }) => (
                          <Select
                            {...field}
                            isMulti
                            className="text-sm"
                            classNamePrefix="react-select"
                            options={districtOptions[index] || []}
                            value={(districtOptions[index] || []).filter((opt) =>
                              (field.value || []).includes(opt.value)
                            )}
                            onChange={(selected) =>
                              handleDistrictChange(selected, index)
                            }
                            closeMenuOnSelect={false}
                            placeholder="Select districts"
                          />
                        )}
                      />
                    </div>

                    <div className="flex flex-col gap-1">
                      <label className="text-sm text-slate-700">
                        Constituencies
                      </label>
                      <Controller
                        control={control}
                        name={`orders.${index}.constituency_ids`}
                        render={({ field }) => (
                          <Select
                            {...field}
                            isMulti
                            className="text-sm"
                            classNamePrefix="react-select"
                            options={constituencyOptions[index] || []}
                            value={(constituencyOptions[index] || []).filter(
                              (opt) => (field.value || []).includes(opt.value)
                            )}
                            onChange={(selected) =>
                              setValue(
                                `orders.${index}.constituency_ids`,
                                selected?.map((item) => item.value) || [],
                                { shouldValidate: true }
                              )
                            }
                            closeMenuOnSelect={false}
                            placeholder="Select constituencies"
                          />
                        )}
                      />
                    </div>
                  </div>

                  <div className="mt-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 items-center">
                    <div className="flex flex-col gap-1">
                      <label className="text-sm text-slate-700">
                        Quantity
                      </label>
                      <input
                        type="number"
                        min="0"
                        {...register(`orders.${index}.quantity`)}
                        className="form-control"
                        placeholder="e.g. 10"
                      />
                      {orderError.quantity && (
                        <span className="text-xs text-red-600">
                          {orderError.quantity.message}
                        </span>
                      )}
                    </div>

                    <div className="flex flex-col gap-1">
                      <label className="text-sm text-slate-700">Price</label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        {...register(`orders.${index}.price`)}
                        className="form-control"
                        placeholder="e.g. 4999"
                      />
                      {orderError.price && (
                        <span className="text-xs text-red-600">
                          {orderError.price.message}
                        </span>
                      )}
                    </div>

                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          {...register(`orders.${index}.internal`)}
                          className="h-4 w-4 rounded border-slate-300 text-slate-700 focus:ring-slate-500"
                          id={`internal-${field.id}`}
                        />
                        <label
                          htmlFor={`internal-${field.id}`}
                          className="text-sm text-slate-700"
                        >
                          Internal
                        </label>
                      </div>
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          {...register(`orders.${index}.gst`)}
                          className="h-4 w-4 rounded border-slate-300 text-slate-700 focus:ring-slate-500"
                          id={`gst-${field.id}`}
                        />
                        <label
                          htmlFor={`gst-${field.id}`}
                          className="text-sm text-slate-700"
                        >
                          GST
                        </label>
                      </div>
                    </div>
                  </div>

                  <div className="mt-4 grid grid-cols-1">
                    <div className="flex flex-col gap-1">
                      <label className="text-sm text-slate-700">Date range</label>
                      <Controller
                        control={control}
                        name={`orders.${index}.dateRange`}
                        render={({ field }) => (
                          <div className="w-full">
                            <Datepicker
                              value={
                                field.value || { startDate: null, endDate: null }
                              }
                              onChange={(val) => field.onChange(val)}
                              useRange
                              asSingle={false}
                              primaryColor="slate"
                              popoverDirection="down"
                              readOnly={false}
                              showShortcuts={false}
                              minDate={today}
                              toggleClassName="hidden"
                              containerClassName="w-full relative"
                              inputClassName="form-control"
                              displayFormat="DD/MM/YYYY"
                            />
                          </div>
                        )}
                      />
                      {(orderError?.dateRange?.startDate ||
                        orderError?.dateRange?.endDate ||
                        orderError?.dateRange?.message) && (
                        <span className="text-xs text-red-600">
                          {orderError?.dateRange?.startDate?.message ||
                            orderError?.dateRange?.endDate?.message ||
                            orderError?.dateRange?.message}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </details>
            );
          })}

          <div className="flex flex-wrap gap-3 rounded-lg border border-slate-200 bg-white px-4 py-3 shadow-sm">
            <button
              type="button"
              onClick={() => append(defaultOrder)}
              className="btn btn-outline-primary d-inline-flex align-items-center gap-2 text-sm"
            >
              <span className="text-lg leading-none">+</span>
              Add another
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="btn btn-primary text-sm"
            >
              {isSubmitting ? "Saving..." : "Save all orders"}
            </button>
          </div>
        </form>
      </div>
    </div>
    </SlDrawer>
  );
}

export default MultiOrder
